"use client"

import { useState, useEffect, useRef } from "react"
import { Sparkles, Download, NotebookPen, Check } from "lucide-react"
import html2canvas from "html2canvas"
import { postAws } from "../aws"

const MnemonicDevice = ({ message, book }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isSaved, setIsSaved] = useState(false)
  const componentRef = useRef(null)

  useEffect(() => {
    setIsVisible(true)
  }, [])

  if (!message || !message.mnemonicDevice) {
    return null
  }

  const { shortForm, explanation } = message.mnemonicDevice

  const createStructuredContent = () => {
    return explanation
      .split("\n")
      .map((line) => {
        if (line.includes("-")) {
          const [letter, ...rest] = line.split("-")
          // Check if the letter is wrapped in double asterisks
          const isBold = /^\*\*.*\*\*$/.test(letter.trim())
          // Remove asterisks from the letter
          const cleanLetter = letter.replace(/\*/g, "").trim()
          return {
            letter: cleanLetter,
            explanation: rest.join("-").trim(),
            isBold,
          }
        }
        return null
      })
      .filter(Boolean)
  }

  const content = createStructuredContent()

  const handleDownload = async () => {
    if (componentRef.current) {
      const canvas = await html2canvas(componentRef.current, {
        scale: 2,
        backgroundColor: null,
      })
      const image = canvas.toDataURL("image/png")
      const link = document.createElement("a")
      link.href = image
      link.download = "mnemonic-device.png"
      link.click()
    }
  }

  const handleSaveToNotes = async () => {
    const notesData = {
      classRoomId: localStorage.getItem("classRoomId"),
      noteType: "mnemonicDevice",
      noteTitle: `${book.fileTitle} Mnemonic`,
      refTitle: book.fileTitle,
      refId: book._id,
      userId: localStorage.getItem("teacherId"),
      noteContent: JSON.stringify({
        message,
      }),
      mediaUrl: "",
    }

    const saveNote = await postAws("https://api-dev.aurait.ai/notes", "POST", notesData)
    if (saveNote.statusCode === 201) {
      setIsSaved(true)
    }
  }

  return (
    <div className="w-full max-w-4xl mx-auto p-6 bg-gray-50">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-2">
          <Sparkles className="text-green-500 animate-bounce" />
          <h3 className="text-lg font-semibold text-indigo-600">Mnemonic Device</h3>
        </div>
        <div className="flex items-center gap-4 -mt-3">
          {book && (
            <div className="relative">

              {isHovered && !isSaved && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-md whitespace-nowrap shadow-lg">
                  Save to Notes
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                </div>
              )}
            </div>
          )}
          <button
            onClick={handleDownload}
            className="flex items-center gap-1 px-2 py-1 text-sm bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 mr-5"
          >
            <Download size={14} />
          </button>
        </div>
      </div>

      <div ref={componentRef}>
        {/* Mnemonic Word Display */}
        <div className="bg-white rounded-2xl shadow-md p-8 mb-8">
          <div className="text-center text-4xl font-bold text-[#1e2a4a] tracking-widest">{shortForm}</div>
        </div>

        {/* Explanation Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {content.map((item, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="flex h-full">
                <div className="bg-[#4263eb] w-3 flex-shrink-0" />
                <div className="flex-1 p-4">
                  <div className="mb-2">
                    <span className={`text-xl text-[#1e2a4a] ${item.isBold ? "font-bold" : "font-normal"}`}>
                      {item.letter}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.explanation}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default MnemonicDevice
